// Inbox TanStack Query hooks — 消费 /api/v1/messages 接口。
// DTO 契约走 packages/shared/dto（F10：前后端共用）。

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import { api } from './api.js';

import type {
  AttachmentSummaryDTO,
  MessageDTO,
  MessageDetailDTO,
  MessageListResult,
} from '@aliashub/shared/dto';

export type InboxTab = 'all' | 'unread' | 'read' | 'forwarded' | 'trash';
export type {
  AttachmentSummaryDTO,
  MessageDTO,
  MessageDetailDTO,
  MessageListResult,
} from '@aliashub/shared/dto';
/** 兼容别名：详情页附件卡片使用 */
export type AttachmentDTO = AttachmentSummaryDTO;

const PAGE_SIZE = 20;

export interface MessagesFilter {
  tab?: InboxTab;
  aliasId?: number | undefined;
  domainId?: number | undefined;
  fromDomain?: string | undefined;
  hasAttachment?: boolean | undefined;
  dateStart?: number | undefined;
  dateEnd?: number | undefined;
  /** 'inbox' = 只看未进文件夹的邮件；number = 某个文件夹；缺省 = 不限 */
  folder?: 'inbox' | number | undefined;
}

function buildListUrl(filter: MessagesFilter = {}, cursor?: string): string {
  const tab = filter.tab ?? 'all';
  const params = new URLSearchParams({ tab, limit: String(PAGE_SIZE) });
  if (filter.aliasId != null) params.set('aliasId', String(filter.aliasId));
  if (filter.domainId != null) params.set('domainId', String(filter.domainId));
  if (filter.fromDomain && filter.fromDomain.trim().length > 0)
    params.set('fromDomain', filter.fromDomain.trim());
  if (filter.hasAttachment != null) params.set('hasAttachment', String(filter.hasAttachment));
  if (filter.dateStart != null) params.set('dateStart', String(filter.dateStart));
  if (filter.dateEnd != null) params.set('dateEnd', String(filter.dateEnd));
  if (filter.folder != null) params.set('folder', String(filter.folder));
  if (cursor) params.set('cursor', cursor);
  return `/api/v1/messages?${params.toString()}`;
}

// ---- 查询 ----

export function useMessages(tab: InboxTab) {
  return useInfiniteQuery<
    MessageListResult,
    Error,
    InfiniteData<MessageListResult, string | undefined>,
    readonly ['messages', 'list', InboxTab],
    string | undefined
  >({
    queryKey: ['messages', 'list', tab] as const,
    queryFn: ({ pageParam }) => api.get<MessageListResult>(buildListUrl({ tab }, pageParam)),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

/** 带高级筛选的 messages 列表（InboxPage 高级筛选区用） */
export function useFilteredMessages(filter: MessagesFilter) {
  return useInfiniteQuery<
    MessageListResult,
    Error,
    InfiniteData<MessageListResult, string | undefined>,
    readonly ['messages', 'filter', MessagesFilter],
    string | undefined
  >({
    queryKey: ['messages', 'filter', filter] as const,
    queryFn: ({ pageParam }) => api.get<MessageListResult>(buildListUrl(filter, pageParam)),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useMessage(id: number) {
  return useQuery<MessageDetailDTO>({
    queryKey: ['messages', 'detail', id] as const,
    queryFn: () => api.get<MessageDetailDTO>(`/api/v1/messages/${id}`),
    staleTime: 30_000,
    enabled: Number.isFinite(id) && id > 0,
  });
}

// ---- 突变（乐观更新） ----

type ListSnapshot = ReturnType<ReturnType<typeof useQueryClient>['getQueriesData']>;

interface OptimisticContext {
  listSnapshots: ListSnapshot;
  detailSnapshot: MessageDetailDTO | undefined;
}

/** 列表查询 key → 对应 tab（'list' 直接带 tab，'filter' 在 filter 对象里）。 */
function tabOfKey(key: readonly unknown[]): InboxTab {
  if (key[1] === 'filter') return (key[2] as MessagesFilter | undefined)?.tab ?? 'all';
  return (key[2] as InboxTab | undefined) ?? 'all';
}

/** 读状态变了之后，这封邮件还属不属于该 tab（未读里读过的要立刻消失，别返回列表还看到它）。 */
function stillInTab(m: MessageDTO, tab: InboxTab): boolean {
  if (tab === 'unread') return m.readAt == null;
  if (tab === 'read') return m.readAt != null;
  return true;
}

// 收件箱 / 文件夹页走 ['messages','filter']，旧入口走 ['messages','list']，两类都要乐观改。
const LIST_PREFIXES = [
  ['messages', 'list'],
  ['messages', 'filter'],
] as const;

function patchLists(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
  patch: (m: MessageDTO) => MessageDTO,
): void {
  for (const prefix of LIST_PREFIXES) {
    const entries = qc.getQueriesData<InfiniteData<MessageListResult, string | undefined>>({
      queryKey: prefix,
    });
    for (const [key, old] of entries) {
      if (!old) continue;
      const tab = tabOfKey(key);
      qc.setQueryData<InfiniteData<MessageListResult, string | undefined>>(key, {
        ...old,
        pages: old.pages.map((p) => ({
          ...p,
          items: p.items.flatMap((m) => {
            if (m.id !== id) return [m];
            const next = patch(m);
            return stillInTab(next, tab) ? [next] : [];
          }),
        })),
      });
    }
  }
}

function patchDetail(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
  patch: (m: MessageDetailDTO) => MessageDetailDTO,
): void {
  const key = ['messages', 'detail', id] as const;
  const cur = qc.getQueryData<MessageDetailDTO>(key);
  if (cur) qc.setQueryData<MessageDetailDTO>(key, patch(cur));
}

async function snapshotAndCancel(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
): Promise<OptimisticContext> {
  await qc.cancelQueries({ queryKey: ['messages'] });
  const listSnapshots = LIST_PREFIXES.flatMap((prefix) => qc.getQueriesData({ queryKey: prefix }));
  const detailSnapshot = qc.getQueryData<MessageDetailDTO>(['messages', 'detail', id]);
  return { listSnapshots, detailSnapshot };
}

function rollback(
  qc: ReturnType<typeof useQueryClient>,
  id: number,
  ctx: OptimisticContext | undefined,
): void {
  if (!ctx) return;
  for (const [key, data] of ctx.listSnapshots) qc.setQueryData(key, data);
  if (ctx.detailSnapshot) qc.setQueryData(['messages', 'detail', id], ctx.detailSnapshot);
}

export function useMarkRead(id: number) {
  const qc = useQueryClient();
  return useMutation<void, Error, void, OptimisticContext>({
    mutationFn: () => api.post<void>(`/api/v1/messages/${id}/read`),
    onMutate: async () => {
      const ctx = await snapshotAndCancel(qc, id);
      const now = Math.floor(Date.now() / 1000);
      patchLists(qc, id, (m) => ({ ...m, readAt: now }));
      patchDetail(qc, id, (m) => ({ ...m, readAt: now }));
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, id, ctx),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}

export function useMarkUnread(id: number) {
  const qc = useQueryClient();
  return useMutation<void, Error, void, OptimisticContext>({
    mutationFn: () => api.post<void>(`/api/v1/messages/${id}/unread`),
    onMutate: async () => {
      const ctx = await snapshotAndCancel(qc, id);
      patchLists(qc, id, (m) => ({ ...m, readAt: null }));
      patchDetail(qc, id, (m) => ({ ...m, readAt: null }));
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, id, ctx),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}

export function useToggleStar(id: number, starred: boolean) {
  const qc = useQueryClient();
  return useMutation<void, Error, void, OptimisticContext>({
    mutationFn: () => api.post<void>(`/api/v1/messages/${id}/${starred ? 'unstar' : 'star'}`),
    onMutate: async () => {
      const ctx = await snapshotAndCancel(qc, id);
      const next = !starred;
      patchLists(qc, id, (m) => ({ ...m, starred: next }));
      patchDetail(qc, id, (m) => ({ ...m, starred: next }));
      return ctx;
    },
    onError: (_e, _v, ctx) => rollback(qc, id, ctx),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}

// 附件下载 URL（同源 GET；browser 会自动带 credentials cookie）
export function attachmentBlobUrl(attachmentId: number): string {
  return `/api/v1/attachments/${attachmentId}/blob`;
}

// ---- 回收站 ----
// 单封删除也走批量端点（前端传 [id]），后端只有一条代码路径。
// 所有回收站突变都不做乐观更新：涉及"这封邮件还在不在"，猜错了比等一下刷新更糟。

export interface BulkAffected {
  affected: number;
}

export interface PurgeResult {
  r2Deleted: number;
  r2Errors: number;
  messagesDeleted: number;
}

function useMessagesMutation<T>(path: string) {
  const qc = useQueryClient();
  return useMutation<T, Error, number[]>({
    mutationFn: (ids: number[]) => api.post<T>(path, { ids }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['trash-count'] });
      // 搜索结果排除回收站：删除/恢复后命中集合会变
      void qc.invalidateQueries({ queryKey: ['advanced-search'] });
    },
  });
}

/** 移入回收站（可恢复） */
export function useTrashMessages() {
  return useMessagesMutation<BulkAffected>('/api/v1/messages/trash');
}

/** 从回收站恢复回收件箱 */
export function useRestoreMessages() {
  return useMessagesMutation<BulkAffected>('/api/v1/messages/restore');
}

/** 彻底删除（不可逆，连 R2 附件与原始 EML 一起删）；后端只接受已在回收站的 id */
export function usePurgeMessages() {
  return useMessagesMutation<PurgeResult>('/api/v1/messages/purge');
}

/** 清空回收站；单轮最多 500 封，remaining > 0 表示要再点一次 */
export function useEmptyTrash() {
  const qc = useQueryClient();
  return useMutation<PurgeResult & { remaining: number }, Error, void>({
    mutationFn: () => api.post<PurgeResult & { remaining: number }>('/api/v1/messages/trash/empty'),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
      void qc.invalidateQueries({ queryKey: ['trash-count'] });
    },
  });
}

/** 回收站封数（侧栏徽标 + 页头计数） */
export function useTrashCount() {
  return useQuery<{ count: number }>({
    queryKey: ['trash-count'] as const,
    queryFn: () => api.get<{ count: number }>('/api/v1/messages/trash/count'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
